import { SERVICE_CITIES, type ServiceCity } from '../data/cityMaterialContent'

/**
 * Resolve a free-text delivery address to one of the cities we publish a
 * page for.
 *
 * Geocoded addresses come back as "12802 219th Avenue East, Bonney Lake, WA,
 * 98391" — the city sits in a middle comma-separated field, not at a fixed
 * index, so this looks for a service-city name in any of them rather than
 * trusting position.
 *
 * Matching is per-segment and exact (case-insensitive) rather than a substring
 * scan of the whole string. A naive `includes` would match "Kent" inside
 * "Kentwood Drive" or "Auburn" inside "Auburn Way", quietly claiming the wrong
 * city — and the city page carries a specific haul fee, so a wrong match is a
 * wrong price.
 *
 * Returns null when the address is still being typed, is outside the service
 * area, or lands in a city we have no page for. Callers must render nothing in
 * that case: most addresses are NOT in the directory, so the absence has to be
 * the silent default.
 */
export function matchServiceCity(address: string): ServiceCity | null {
  if (!address) return null

  const segments = address
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)

  if (segments.length === 0) return null

  for (const city of SERVICE_CITIES) {
    const name = city.name.toLowerCase()
    if (segments.some((segment) => segment === name)) return city
  }

  return null
}
